import { Button, Table, TableBody, TableCell, TableRow } from "@mui/material";
import clsx from "clsx";
import React, { useState } from "react";
import {
  PipedaOfficerValues,
  EditPipedaPrivacyOfficerDialog,
} from "../../../dialog/EditPipedaPrivacyOfficerDialog";
import { SettingRow } from "./SettingRow";
import { OrganizationDto } from "../../../../../common/service/server/dto/OrganizationDto";
import { Button as AquiloButton } from "@truevault/aquilo";
import { CheckIcon } from "@heroicons/react/24/solid";

type Props = {
  organization: OrganizationDto;
  dpoName: string | undefined;
  dpoEmail: string | undefined;
  dpoPhone: string | undefined;
  dpoAddress: string | undefined;
  pipedaOfficerName: string | undefined;
  pipedaOfficerEmail: string | undefined;
  pipedaOfficerPhone: string | undefined;
  pipedaOfficerAddress: string | undefined;
  handleFormSubmit: (values) => void;
  title?: string;
  helpText?: React.ReactNode;
  vertical?: boolean;
  showSkip?: boolean;
  useNewDesign?: boolean;
  // In the review page, render smaller text + an extra-small button to match the other rows
  compact?: boolean;
};

export const PrivacyCenterPipedaPrivacyOfficerRow: React.FC<Props> = ({
  organization,
  dpoName,
  dpoEmail,
  dpoPhone,
  dpoAddress,
  pipedaOfficerName,
  pipedaOfficerEmail,
  pipedaOfficerPhone,
  pipedaOfficerAddress,
  handleFormSubmit,
  title = "Canadian privacy laws require your organization to name a privacy officer in your online privacy policy.",
  helpText = "Your privacy officer is a person either inside your organization or external to it (such as a Data Privacy Officer (DPO) who is accountable for PIPEDA compliance. You can put the name of a member of your privacy team and your new privacy@yourdomain email address as the contact email.",
  vertical = false,
  useNewDesign = false,
  compact = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasCanadianPO = Boolean(pipedaOfficerName && pipedaOfficerEmail);

  // New design (survey card): standard question-card help text size (text-[20px], neutral-700).
  // Compact (review page): 18px medium #332E42 to match the other review rows.
  const compactTextStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: 500,
    color: "#332E42",
  };
  const renderNewDesignText = (content: React.ReactNode) =>
    compact ? (
      <span style={compactTextStyle}>{content}</span>
    ) : (
      <span className="text-[20px] text-neutral-700 font-normal">{content}</span>
    );
  const titleNode = useNewDesign ? renderNewDesignText(title) : title;
  const helpTextNode = useNewDesign ? renderNewDesignText(helpText) : helpText;

  return (
    <SettingRow title={titleNode} helpText={helpTextNode} vertical={vertical}>
      {hasCanadianPO && !useNewDesign && (
        <Table className="mt-0">
          <TableBody>
            <TableRow>
              <TableCell width="125px">Name or Title:</TableCell>
              <TableCell>{pipedaOfficerName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Email:</TableCell>
              <TableCell>{pipedaOfficerEmail}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Telephone:</TableCell>
              <TableCell>{pipedaOfficerPhone}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Address:</TableCell>
              <TableCell>{pipedaOfficerAddress}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      <div
        className={clsx(
          "align-items-center",
          { "flex-col": !vertical, "flex-row": vertical },
          !compact && "mt-md",
        )}
        style={compact ? { marginTop: "6px" } : undefined}
      >
        {useNewDesign ? (
          <AquiloButton
            color="primary"
            size={compact ? "xs" : "m"}
            onClick={() => setDialogOpen(true)}
          >
            {hasCanadianPO ? (
              <span className="inline-flex items-center gap-[8px]">
                <CheckIcon className="w-[20px] h-[20px]" />
                Privacy officer details were added
              </span>
            ) : (
              "Set Privacy Officer"
            )}
          </AquiloButton>
        ) : (
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            {hasCanadianPO ? "Edit Privacy Officer" : "Set Privacy Officer"}
          </Button>
        )}
      </div>

      <EditPipedaPrivacyOfficerDialog
        organization={organization}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onDone={(values: PipedaOfficerValues) =>
          handleFormSubmit({
            pipedaOfficerName: values.name,
            pipedaOfficerEmail: values.email,
            pipedaOfficerPhone: values.phone,
            pipedaOfficerAddress: values.address,
          })
        }
        values={{
          name: pipedaOfficerName,
          email: pipedaOfficerEmail,
          phone: pipedaOfficerPhone,
          address: pipedaOfficerAddress,
        }}
        dpo={
          dpoName && dpoEmail
            ? {
                name: dpoName,
                email: dpoEmail,
                phone: dpoPhone,
                address: dpoAddress,
              }
            : null
        }
      />
    </SettingRow>
  );
};
